import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Mountain, X } from "lucide-react";

const STATUS = {
  on_track: { label: "On track", cls: "bg-emerald-100 text-emerald-700", next: "off_track" },
  off_track: { label: "Off track", cls: "bg-red-100 text-red-700", next: "done" },
  done: { label: "Done", cls: "bg-slate-200 text-slate-700", next: "on_track" },
};

const EMPTY = { title: "", description: "", type: "company", owner_id: "", owner_name: "", due_date: "", milestones: [] };

const initials = (n) => (n || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
const fmtDue = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-AU", { day: "2-digit", month: "short" }) : "—");

function StatTile({ label, value, suffix, pct, barCls = "bg-emerald-500", testid }) {
  return (
    <Card className="p-4 rounded-sm border-slate-200 shadow-none" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">{label}</div>
      <div className="font-mono text-2xl mt-1 text-slate-900">
        {value} {suffix && <span className="text-xs text-slate-400 font-sans">{suffix}</span>}
      </div>
      <div className="h-1.5 bg-slate-100 rounded-sm mt-2 overflow-hidden">
        <div className={`h-full ${barCls}`} style={{ width: `${Math.min(pct || 0, 100)}%` }} />
      </div>
    </Card>
  );
}

function MilestoneBar({ milestones }) {
  const total = milestones?.length || 0;
  const done = (milestones || []).filter((m) => m.done).length;
  const pct = total ? (done / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 bg-slate-100 rounded-sm overflow-hidden">
        <div className={`h-full ${pct < 40 ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="font-mono text-xs text-slate-500">{done}/{total}</span>
    </div>
  );
}

function RockTable({ title, rocks, onEdit, onStatus }) {
  return (
    <Card className="rounded-sm border-slate-200 shadow-none overflow-hidden mb-5" data-testid={`rocks-table-${title.toLowerCase().replace(/ /g, "-")}`}>
      <div className="px-4 py-2.5 border-b border-slate-200 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">{title}</div>
      {rocks.length === 0 ? (
        <div className="px-4 py-6 text-sm text-slate-400" data-testid="rocks-empty">No rocks yet — add one for this quarter.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                <th className="text-left px-4 py-2 font-semibold w-[40%]">Rock</th>
                <th className="text-left px-4 py-2 font-semibold">Owner</th>
                <th className="text-left px-4 py-2 font-semibold">Milestones</th>
                <th className="text-left px-4 py-2 font-semibold">Status</th>
                <th className="text-left px-4 py-2 font-semibold">Due</th>
              </tr>
            </thead>
            <tbody>
              {rocks.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => onEdit(r)} data-testid={`rock-row-${r.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="font-semibold text-slate-900">{r.title}</div>
                    {r.description && <div className="text-xs text-slate-500 mt-0.5">{r.description}</div>}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 bg-slate-800 text-white text-[10px] font-bold flex items-center justify-center rounded-sm">{initials(r.owner_name)}</span>
                      <span className="text-slate-700">{r.owner_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5"><MilestoneBar milestones={r.milestones} /></td>
                  <td className="px-4 py-2.5">
                    <button
                      data-testid={`rock-status-${r.id}`}
                      onClick={(e) => { e.stopPropagation(); onStatus(r); }}
                      title="Click to change status"
                      className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm ${STATUS[r.status]?.cls || ""}`}>
                      {STATUS[r.status]?.label || r.status}
                    </button>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{fmtDue(r.due_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export default function ManagementRocks() {
  const qc = useQueryClient();
  const [quarter, setQuarter] = useState(null);
  const [editing, setEditing] = useState(null); // null | form object

  const { data: qData } = useQuery({ queryKey: ["mgmt-quarters"], queryFn: async () => (await api.get("/mgmt/quarters")).data });
  const selected = quarter || qData?.current;
  const { data } = useQuery({
    queryKey: ["mgmt-rocks", selected],
    queryFn: async () => (await api.get(`/mgmt/rocks?quarter=${selected}`)).data,
    enabled: !!selected,
  });
  const { data: team } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/auth/team")).data });

  const rocks = data?.rocks || [];
  const stats = data?.stats || {};
  const company = rocks.filter((r) => r.type === "company");
  const individual = rocks.filter((r) => r.type === "individual");
  const invalidate = () => qc.invalidateQueries({ queryKey: ["mgmt-rocks", selected] });

  const saveMut = useMutation({
    mutationFn: async (form) => {
      const payload = {
        title: form.title, description: form.description || null, type: form.type,
        owner_id: form.owner_id || null, owner_name: form.owner_name,
        due_date: form.due_date || null,
        milestones: (form.milestones || []).filter((m) => m.title.trim()),
        ...(form.id && form.status ? { status: form.status } : {}),
      };
      if (form.id) return (await api.patch(`/mgmt/rocks/${form.id}`, payload)).data;
      return (await api.post("/mgmt/rocks", { ...payload, quarter: selected })).data;
    },
    onSuccess: () => { invalidate(); setEditing(null); toast.success("Rock saved"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not save rock"),
  });

  const statusMut = useMutation({
    mutationFn: async (r) => (await api.patch(`/mgmt/rocks/${r.id}`, { status: STATUS[r.status]?.next || "on_track" })).data,
    onSuccess: invalidate,
    onError: (e) => toast.error(e.response?.data?.detail || "Could not update status"),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/mgmt/rocks/${id}`)).data,
    onSuccess: () => { invalidate(); setEditing(null); toast.success("Rock deleted"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not delete rock"),
  });

  const quarterInfo = useMemo(() => (qData?.quarters || []).find((q) => q.key === selected), [qData, selected]);

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (r) => setEditing({ ...r, description: r.description || "", due_date: r.due_date || "", milestones: (r.milestones || []).map((m) => ({ ...m })) });

  const setForm = (patch) => setEditing((f) => ({ ...f, ...patch }));

  return (
    <div data-testid="management-rocks-page">
      <PageHeader
        title={`Quarterly Rocks${quarterInfo ? ` — ${quarterInfo.label}` : ""}`}
        subtitle={quarterInfo ? `${fmtDue(quarterInfo.start)} – ${fmtDue(quarterInfo.end)} · 90-day priorities · day ${stats.days_elapsed || 0} of ${stats.days_total || 0}` : "90-day priorities"}
        actions={
          <>
            <div className="hidden sm:inline-flex border border-slate-300 rounded-sm overflow-hidden" data-testid="quarter-switcher">
              {(qData?.quarters || []).slice(1, 5).map((q) => (
                <button key={q.key} onClick={() => setQuarter(q.key)} data-testid={`quarter-btn-${q.key}`}
                  className={`px-3 py-1.5 text-xs font-semibold ${q.key === selected ? "bg-slate-800 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
                  {q.label}{q.current ? " ●" : ""}
                </button>
              ))}
            </div>
            <Button onClick={openNew} className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" data-testid="add-rock-btn"><Plus size={15} className="mr-1" />Add Rock</Button>
          </>
        }
      />
      <div className="px-4 sm:px-6 lg:px-10 pb-24">
      <div className="sm:hidden mb-4" data-testid="quarter-switcher-mobile">
        <Select value={selected || ""} onValueChange={setQuarter}>
          <SelectTrigger className="rounded-sm"><SelectValue placeholder="Quarter" /></SelectTrigger>
          <SelectContent>
            {(qData?.quarters || []).map((q) => <SelectItem key={q.key} value={q.key}>{q.label}{q.current ? " (current)" : ""}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatTile label="Company rocks" value={stats.company_total ?? 0} suffix="set" testid="stat-company"
          pct={stats.company_total ? 100 : 0} />
        <StatTile label="On track / done" value={(stats.on_track ?? 0) + (stats.done ?? 0)} suffix={`/ ${rocks.length}`} testid="stat-ontrack"
          pct={rocks.length ? ((stats.on_track + stats.done) / rocks.length) * 100 : 0} />
        <StatTile label="Quarter completion" value={`${stats.elapsed_pct ?? 0}%`} suffix="time elapsed" testid="stat-elapsed"
          pct={stats.elapsed_pct} barCls="bg-cyan-600" />
        <StatTile label="Milestones done" value={stats.milestones_done ?? 0} suffix={`/ ${stats.milestones_total ?? 0}`} testid="stat-milestones"
          pct={stats.milestones_total ? (stats.milestones_done / stats.milestones_total) * 100 : 0} />
      </div>

      <RockTable title="Company Rocks" rocks={company} onEdit={openEdit} onStatus={(r) => statusMut.mutate(r)} />
      <RockTable title="Individual Rocks" rocks={individual} onEdit={openEdit} onStatus={(r) => statusMut.mutate(r)} />
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-y-auto" data-testid="rock-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Mountain size={18} className="text-cyan-600" />{editing?.id ? "Edit Rock" : "New Rock"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Rock title *</label>
                <Input value={editing.title} onChange={(e) => setForm({ title: e.target.value })} placeholder="e.g. Win 2 council panel contracts" className="rounded-sm mt-1" data-testid="rock-title-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Description</label>
                <Textarea value={editing.description} onChange={(e) => setForm({ description: e.target.value })} rows={2} className="rounded-sm mt-1" data-testid="rock-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Type</label>
                  <Select value={editing.type} onValueChange={(v) => setForm({ type: v })}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="rock-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="company">Company rock</SelectItem>
                      <SelectItem value="individual">Individual rock</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Owner *</label>
                  <Select value={editing.owner_id || ""} onValueChange={(v) => {
                    const m = (team || []).find((t) => t.id === v);
                    setForm({ owner_id: v, owner_name: m?.name || "" });
                  }}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="rock-owner-select"><SelectValue placeholder="Pick owner" /></SelectTrigger>
                    <SelectContent>
                      {(team || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Due date</label>
                  <Input type="date" value={editing.due_date} onChange={(e) => setForm({ due_date: e.target.value })} className="rounded-sm mt-1" data-testid="rock-due-input" />
                </div>
                {editing.id && (
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Status</label>
                    <Select value={editing.status} onValueChange={(v) => setForm({ status: v })}>
                      <SelectTrigger className="rounded-sm mt-1" data-testid="rock-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="on_track">On track</SelectItem>
                        <SelectItem value="off_track">Off track</SelectItem>
                        <SelectItem value="done">Done</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Milestones</label>
                <div className="space-y-1.5 mt-1">
                  {(editing.milestones || []).map((m, i) => (
                    <div key={m.id || i} className="flex items-center gap-2" data-testid={`milestone-row-${i}`}>
                      <input type="checkbox" checked={!!m.done} data-testid={`milestone-done-${i}`}
                        onChange={(e) => setForm({ milestones: editing.milestones.map((x, j) => j === i ? { ...x, done: e.target.checked } : x) })}
                        className="w-4 h-4 accent-emerald-600" />
                      <Input value={m.title} placeholder="Milestone"
                        onChange={(e) => setForm({ milestones: editing.milestones.map((x, j) => j === i ? { ...x, title: e.target.value } : x) })}
                        className={`rounded-sm h-8 text-sm flex-1 ${m.done ? "line-through text-slate-400" : ""}`} data-testid={`milestone-title-${i}`} />
                      <button onClick={() => setForm({ milestones: editing.milestones.filter((_, j) => j !== i) })}
                        className="text-slate-400 hover:text-red-600" data-testid={`milestone-remove-${i}`}><X size={14} /></button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" className="rounded-sm text-xs" data-testid="add-milestone-btn"
                    onClick={() => setForm({ milestones: [...(editing.milestones || []), { title: "", done: false }] })}>
                    <Plus size={13} className="mr-1" />Add milestone
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            {editing?.id ? (
              <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-sm" data-testid="delete-rock-btn"
                onClick={() => window.confirm("Delete this rock?") && delMut.mutate(editing.id)}>
                <Trash2 size={15} className="mr-1" />Delete
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setEditing(null)} data-testid="cancel-rock-btn">Cancel</Button>
              <Button className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" data-testid="save-rock-btn"
                disabled={!editing?.title?.trim() || !editing?.owner_name || saveMut.isPending}
                onClick={() => saveMut.mutate(editing)}>
                {editing?.id ? "Save changes" : "Add Rock"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
